import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { AuthContext, API } from '../App';
import {
  Briefcase, Copy, Check, Loader2, ChevronDown, Lock,
  MessageSquare, Sparkles, ArrowLeft, Clock, Building2
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { PackageBanner } from '../components/PackageBanner';
import axios from 'axios';
import { toast } from 'sonner';

const JOB_ROLES = ['Sales', 'Admin', 'IT', 'Customer Service', 'Hospitality', 'Call Center', 'Marketing', 'HR'];
const EXPERIENCE_LEVELS = ['Fresher', 'Experienced'];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#009639] transition-colors px-2 py-1 rounded hover:bg-gray-100"
      data-testid="copy-answer-btn"
    >
      {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
    </button>
  );
}

export default function InterviewPackPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Form
  const [jobRole, setJobRole] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Result
  const [pack, setPack] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('questions');

  useEffect(() => {
    checkAccess();
    fetchHistory();
  }, []);

  const checkAccess = async () => {
    try {
      const res = await axios.get(`${API}/interview-pack/access/check`);
      setHasAccess(res.data.has_access);
    } catch {
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/interview-pack/history`);
      setHistory(res.data);
    } catch {
      // ignore
    }
  };

  const handleGenerate = async () => {
    if (!jobRole) { toast.error('Please select a job role'); return; }
    if (!experienceLevel) { toast.error('Please select experience level'); return; }

    setGenerating(true);
    try {
      const res = await axios.post(`${API}/interview-pack/generate`, {
        job_role: jobRole,
        experience_level: experienceLevel,
        company_name: companyName || null
      });
      setPack(res.data.pack);
      setActiveTab('questions');
      toast.success('Interview pack generated!');
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate interview pack');
    } finally {
      setGenerating(false);
    }
  };

  const loadPack = (p) => {
    setPack(p);
    setActiveTab('questions');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#CE1126]" />
      </div>
    );
  }

  // No access — paywall
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16 max-w-2xl text-center">
          <Card className="p-10">
            <Lock className="w-16 h-16 text-gray-300 mx-auto mb-6" />
            <h1 className="text-3xl font-bold mb-3">Interview Pack</h1>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Get AI-generated interview questions, strong example answers, and English improvement tips — tailored to your role in the UAE job market.
            </p>
            <div className="space-y-3">
              <Link to="/pricing">
                <Button className="btn-uae-red px-8 py-3 text-base" data-testid="unlock-interview-pack-btn">
                  Unlock for AED 149
                </Button>
              </Link>
              <p className="text-sm text-gray-500">
                Also included in the <strong>Pro plan</strong> (AED 49/month) and <strong>Job Apply Kit</strong> (AED 199).
              </p>
            </div>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <PackageBanner />
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Link to="/dashboard" className="text-gray-400 hover:text-gray-600">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <h1 className="text-3xl font-bold" data-testid="interview-pack-title">Interview Pack</h1>
            </div>
            <p className="text-gray-600 ml-7">Generate tailored interview prep for UAE job roles</p>
          </div>
        </div>

        {/* Quick Access to Other Benefits */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Link to="/cv-checker" className="block">
            <Card className="p-3 text-center hover:shadow-md transition-shadow cursor-pointer">
              <p className="text-xs font-medium text-[#CE1126]">ATS CV Fix</p>
            </Card>
          </Link>
          <Link to="/job-apply-kit" className="block">
            <Card className="p-3 text-center hover:shadow-md transition-shadow cursor-pointer">
              <p className="text-xs font-medium text-indigo-600">Job Apply Kit</p>
            </Card>
          </Link>
          <Link to="/interview-test" className="block">
            <Card className="p-3 text-center hover:shadow-md transition-shadow cursor-pointer">
              <p className="text-xs font-medium text-[#009639]">English Test</p>
            </Card>
          </Link>
        </div>

        {/* Input Form */}
        {!pack && (
          <Card className="p-6 mb-8" data-testid="interview-pack-form">
            <h2 className="text-lg font-semibold mb-5">Tell us about your target role</h2>
            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              {/* Job Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Job Role *</label>
                <div className="relative">
                  <select
                    value={jobRole}
                    onChange={e => setJobRole(e.target.value)}
                    className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126] appearance-none"
                    data-testid="job-role-select"
                  >
                    <option value="">Select a role</option>
                    {JOB_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />
                </div>
              </div>

              {/* Experience Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Experience Level *</label>
                <div className="flex gap-3">
                  {EXPERIENCE_LEVELS.map(lvl => (
                    <button
                      key={lvl}
                      onClick={() => setExperienceLevel(lvl)}
                      className={`flex-1 h-10 rounded-md border text-sm font-medium transition-all ${
                        experienceLevel === lvl
                          ? 'border-[#CE1126] bg-[#CE1126] text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                      data-testid={`experience-${lvl.toLowerCase()}-btn`}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Company Name */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Company Name <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="e.g. Emirates NBD, Carrefour, Etisalat..."
                className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
                data-testid="company-name-input"
              />
            </div>

            <Button
              className="btn-uae-red px-8"
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-pack-btn"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Pack...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generate Interview Pack</>
              )}
            </Button>
          </Card>
        )}

        {/* Result Display */}
        {pack && (
          <>
            {/* Pack Info Bar */}
            <Card className="p-4 mb-6 flex flex-wrap items-center justify-between gap-3 bg-gray-900 text-white">
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-[#CE1126]" />
                <span className="font-semibold">{pack.job_role}</span>
                <span className="text-gray-400">|</span>
                <span className="text-sm text-gray-300">{pack.experience_level}</span>
                {pack.company_name && (
                  <>
                    <span className="text-gray-400">|</span>
                    <span className="text-sm text-gray-300 flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5" /> {pack.company_name}
                    </span>
                  </>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-white text-white hover:bg-white hover:text-gray-900"
                onClick={() => setPack(null)}
                data-testid="new-pack-btn"
              >
                New Pack
              </Button>
            </Card>

            {/* Tabs */}
            <div className="flex border-b mb-6 gap-1">
              {[
                { key: 'questions', label: 'Role Questions', icon: MessageSquare, count: pack.role_questions?.length },
                { key: 'optimized', label: 'Key Answers', icon: Sparkles, count: pack.optimized_answers?.length },
                { key: 'english', label: 'English Improvement', icon: Briefcase, count: pack.english_improvement?.length }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-[#CE1126] text-[#CE1126]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`tab-${tab.key}`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{tab.count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content: Role Questions */}
            {activeTab === 'questions' && (
              <div className="space-y-4" data-testid="role-questions-section">
                {(pack.role_questions || []).map((q, i) => (
                  <Card key={i} className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h3 className="font-semibold text-gray-900">
                        <span className="text-[#CE1126] mr-2">Q{i + 1}.</span>
                        {q.question}
                      </h3>
                    </div>
                    <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-[#009639] uppercase tracking-wide">Strong Answer</span>
                        <CopyButton text={q.strong_answer} />
                      </div>
                      <p className="text-sm text-gray-800 leading-relaxed">{q.strong_answer}</p>
                    </div>
                    {q.tip && (
                      <p className="text-xs text-gray-500 italic flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> {q.tip}
                      </p>
                    )}
                  </Card>
                ))}
              </div>
            )}

            {/* Tab Content: Optimized Answers */}
            {activeTab === 'optimized' && (
              <div className="space-y-4" data-testid="optimized-answers-section">
                <p className="text-sm text-gray-600 mb-2">
                  These are the four most-asked questions in UAE interviews. Use these optimized answers as a template.
                </p>
                {(pack.optimized_answers || []).map((a, i) => (
                  <Card key={i} className="p-5 border-l-4 border-l-[#CE1126]">
                    <h3 className="font-semibold text-gray-900 mb-3">{a.question}</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-[#CE1126] uppercase tracking-wide">Optimized Answer</span>
                        <CopyButton text={a.answer} />
                      </div>
                      <p className="text-sm text-gray-800 leading-relaxed">{a.answer}</p>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Tab Content: English Improvement */}
            {activeTab === 'english' && (
              <div className="space-y-5" data-testid="english-improvement-section">
                <p className="text-sm text-gray-600 mb-2">
                  Compare weak answers with professional alternatives. Practice speaking the improved versions out loud.
                </p>
                {(pack.english_improvement || []).map((item, i) => (
                  <Card key={i} className="p-5">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{item.context}</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                        <span className="text-xs font-semibold text-red-600 uppercase tracking-wide block mb-2">Weak Answer</span>
                        <p className="text-sm text-gray-700 leading-relaxed">{item.weak_answer}</p>
                      </div>
                      <div className="bg-green-50 border border-green-100 rounded-lg p-4 relative">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-[#009639] uppercase tracking-wide">Professional Answer</span>
                          <CopyButton text={item.improved_answer} />
                        </div>
                        <p className="text-sm text-gray-800 leading-relaxed">{item.improved_answer}</p>
                      </div>
                    </div>
                    {item.improvement_notes && (
                      <p className="text-xs text-gray-500 mt-3 italic flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> {item.improvement_notes}
                      </p>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* History */}
        {history.length > 0 && !pack && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold mb-4">Previous Packs</h2>
            <div className="space-y-3" data-testid="pack-history">
              {history.map(h => (
                <Card
                  key={h.id}
                  className="p-4 flex items-center justify-between cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => loadPack(h)}
                >
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-[#CE1126]" />
                    <div>
                      <p className="font-medium text-sm">{h.job_role} — {h.experience_level}</p>
                      {h.company_name && <p className="text-xs text-gray-500">{h.company_name}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Clock className="w-3.5 h-3.5" />
                    {new Date(h.created_at).toLocaleDateString()}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
